'use client';

import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { PostCategory, PostTag } from '@routeroyal/theme-core';

export interface PostFiltersState {
  search: string;
  categorySlug: string | null;
  tagSlugs: string[];
  sortBy: 'publishedAt' | 'viewCount' | 'title';
  sortDirection: 'desc' | 'asc';
}

export interface StoneInvestmentPostFiltersProps {
  filters: PostFiltersState;
  onChange: (filters: PostFiltersState) => void;
  categories: PostCategory[];
  tags?: PostTag[];
  showTags?: boolean;
  showSort?: boolean;
  className?: string;
  messages?: {
    searchPlaceholder?: string;
    allCategories?: string;
    allTags?: string;
    sortBy?: string;
    newest?: string;
    oldest?: string;
    popular?: string;
    alphabetical?: string;
    clearFilters?: string;
  };
}

export const defaultPostFilters: PostFiltersState = {
  search: '',
  categorySlug: null,
  tagSlugs: [],
  sortBy: 'publishedAt',
  sortDirection: 'desc',
};

/**
 * Stone Investment Post Filters
 * 
 * Minimal, editorial-style filtering with:
 * - Refined category pills
 * - Elegant sorting dropdown
 * - Sophisticated animations
 */
export function StoneInvestmentPostFilters({
  filters,
  onChange,
  categories,
  tags = [],
  showTags = false,
  showSort = true,
  className = '',
  messages: customMessages = {},
}: StoneInvestmentPostFiltersProps) {
  const messages = {
    searchPlaceholder: 'Search articles...',
    allCategories: 'All',
    allTags: 'All Tags',
    sortBy: 'Sort',
    newest: 'Latest',
    oldest: 'Oldest',
    popular: 'Popular',
    alphabetical: 'A-Z',
    clearFilters: 'Clear',
    ...customMessages,
  };

  const [isSortOpen, setIsSortOpen] = useState(false);

  const updateFilter = useCallback(<K extends keyof PostFiltersState>(
    key: K,
    value: PostFiltersState[K]
  ) => {
    onChange({ ...filters, [key]: value });
  }, [filters, onChange]);

  const toggleTag = useCallback((slug: string) => {
    const newTags = filters.tagSlugs.includes(slug)
      ? filters.tagSlugs.filter(t => t !== slug)
      : [...filters.tagSlugs, slug];
    updateFilter('tagSlugs', newTags);
  }, [filters.tagSlugs, updateFilter]);

  const clearFilters = useCallback(() => {
    onChange(defaultPostFilters);
  }, [onChange]);

  const hasActiveFilters = 
    filters.search !== '' ||
    filters.categorySlug !== null ||
    filters.tagSlugs.length > 0;

  const sortLabelMap: Record<string, string> = {
    'publishedAt-desc': messages.newest,
    'publishedAt-asc': messages.oldest,
    'viewCount-desc': messages.popular,
    'title-asc': messages.alphabetical,
  };
  const sortLabel = sortLabelMap[`${filters.sortBy}-${filters.sortDirection}`] ?? messages.newest;

  return (
    <div className={`space-y-6 ${className}`} style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Category Pills */}
      <div className="flex flex-wrap items-center gap-2 gap-y-3">
        <button
          onClick={() => updateFilter('categorySlug', null)}
          className={`
            px-4 py-2 text-xs tracking-[0.2em] uppercase
            transition-all duration-300
            ${!filters.categorySlug 
              ? 'bg-[var(--color-text)] text-[var(--color-background)]' 
              : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
            }
          `}
        >
          {messages.allCategories}
        </button>
        
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => updateFilter('categorySlug', cat.slug)}
            className={`
              px-4 py-2 text-xs tracking-[0.2em] uppercase
              transition-all duration-300
              ${filters.categorySlug === cat.slug 
                ? 'bg-[var(--color-text)] text-[var(--color-background)]' 
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
              }
            `}
          >
            {cat.name}
          </button>
        ))}

        {/* Sort Dropdown */}
        {showSort && (
          <div className="relative ml-0 sm:ml-auto w-full sm:w-auto">
            <button
              onClick={() => setIsSortOpen(!isSortOpen)}
              className="
                flex items-center gap-2 px-4 py-2
                text-xs tracking-[0.2em] uppercase
                text-[var(--color-text-muted)] hover:text-[var(--color-text)]
                transition-colors duration-300
              "
            >
              <span>{sortLabel}</span>
              <motion.svg
                animate={{ rotate: isSortOpen ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                className="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
              </motion.svg>
            </button>

            <AnimatePresence>
              {isSortOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.15 }}
                  className="
                    absolute right-0 top-full mt-2 py-2
                    bg-[var(--color-surface)] border border-[var(--color-border)]
                    shadow-lg z-10 min-w-[140px]
                  "
                >
                  {[
                    { value: 'publishedAt-desc', label: messages.newest },
                    { value: 'publishedAt-asc', label: messages.oldest },
                    { value: 'viewCount-desc', label: messages.popular },
                    { value: 'title-asc', label: messages.alphabetical },
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => {
                        const [sortBy, sortDirection] = value.split('-') as [PostFiltersState['sortBy'], PostFiltersState['sortDirection']];
                        onChange({ ...filters, sortBy, sortDirection });
                        setIsSortOpen(false);
                      }}
                      className={`
                        w-full px-4 py-2 text-left text-xs tracking-[0.15em] uppercase
                        transition-colors duration-200
                        ${`${filters.sortBy}-${filters.sortDirection}` === value
                          ? 'text-[var(--color-text)] bg-[var(--color-background)]'
                          : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-background)]'
                        }
                      `}
                    >
                      {label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="
              px-4 py-2 text-xs tracking-[0.2em] uppercase
              text-[var(--color-text-muted)] hover:text-[var(--color-text)]
              transition-colors duration-300
            "
          >
            {messages.clearFilters}
          </button>
        )}
      </div>

      {/* Tags */}
      {showTags && tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <button
              key={tag.id}
              onClick={() => toggleTag(tag.slug)}
              className={`
                px-3 py-1.5 text-xs tracking-[0.15em] uppercase
                border transition-all duration-300
                ${filters.tagSlugs.includes(tag.slug)
                  ? 'border-[var(--color-text)] bg-[var(--color-text)] text-[var(--color-background)]'
                  : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-text)] hover:text-[var(--color-text)]'
                }
              `}
            >
              {tag.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default StoneInvestmentPostFilters;
